import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useQueryParamsHandler } from 'odfjell-query-manager';
import HomePage from 'src/pages/home/HomePage';
import ProductsPage from 'src/pages/products/ProductsPage';
import ProductPage from 'src/pages/product/ProductPage';
import LoginPage from 'src/pages/login/LoginPage';
import ChangeLogPageWrapper from 'src/pages/changelog/ChangeLogPageWrapper';
import AdminPage from 'src/pages/admin/AdminPage';
import { PrivateRoute } from './PrivateRoute';
import Layout from 'src/components/Layout';
import { AppWrapper, PageWrapper, ContentWrapper, LoadingWrapper } from '../App.styles';
import ToolsPage from 'src/pages/tools/ToolsPage';
import { useAuthStore } from '../context/AuthContext';
import useMsal from '../auth/useMsal';
import useGetUserRoles from '../api/useGetUserRoles';
import useLoadBaseData from '../hooks/useLoadBaseData';

// Wrapper component for protected routes with shared layout
const ProtectedLayout = ({ children }: { children: React.ReactNode }) => (
    <PrivateRoute>
        <PageWrapper>
            <Layout>
                <ContentWrapper>{children}</ContentWrapper>
            </Layout>
        </PageWrapper>
    </PrivateRoute>
);

const RouterContent = () => {
    const location = useLocation();
    const { checkAuthStatus, initHandleRedirect, isInitialized } = useMsal();
    const { loggedInUser, isAuthenticating, isVerifyingToken } = useAuthStore();
    const { getUserRoles } = useGetUserRoles();
    const { loadBaseData, isBaseDataLoaded } = useLoadBaseData();
    const { triggerAllQueryParamsFromUrl } = useQueryParamsHandler();
    const [, setIsAuthorized] = useState<boolean | undefined>(undefined);

    // Track whether we've already initiated auth check to prevent duplicate calls
    const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

    // Trigger query params update when location changes (like OrcaWebsite)
    useEffect(() => {
        triggerAllQueryParamsFromUrl();
    }, [location]);

    // Like OrcaWebsite: Simple dependency on loggedInUser and isInitialized
    // getUserRoles is now a simple function (not useCallback) so it doesn't change
    useEffect(() => {
        if (loggedInUser) {
            setIsAuthorized(true);
            getUserRoles();

            // Load base product data after user is authenticated
            if (!isBaseDataLoaded) {
                console.log('[RouterContent] Loading base product data...');
                loadBaseData();
            }
        } else if (isInitialized && !hasCheckedAuth) {
            // Only call checkAuthStatus after MSAL is initialized
            setHasCheckedAuth(true);
            checkAuthStatus();
        }
    }, [loggedInUser, isInitialized, isBaseDataLoaded]);

    useEffect(() => {
        // Initialize redirect handling when MSAL is ready (like OrcaWebsite)
        if (isInitialized) {
            initHandleRedirect();
        }
    }, [isInitialized]);

    // Show loading state while verifying token or authenticating
    if (isVerifyingToken || isAuthenticating) {
        return <LoadingWrapper>{isVerifyingToken ? 'Verifying authentication...' : 'Authenticating...'}</LoadingWrapper>;
    }

    return (
        <AppWrapper>
            {!isVerifyingToken && !isAuthenticating && (
                <Routes>
                    {/* Public login route */}
                    <Route path="/login" element={<LoginPage />} />

                    {/* Default route redirects to home */}
                    <Route path="/" element={<Navigate to="/home" replace />} />

                    {/* Protected application routes */}
                    <Route
                        path="/home"
                        element={
                            <ProtectedLayout>
                                <HomePage />
                            </ProtectedLayout>
                        }
                    />
                    <Route
                        path="/products"
                        element={
                            <ProtectedLayout>
                                <ProductsPage />
                            </ProtectedLayout>
                        }
                    />
                    <Route
                        path="/product"
                        element={
                            <ProtectedLayout>
                                <ProductPage />
                            </ProtectedLayout>
                        }
                    />
                    <Route
                        path="/tools"
                        element={
                            <ProtectedLayout>
                                <ToolsPage />
                            </ProtectedLayout>
                        }
                    />
                    <Route
                        path="/changelog"
                        element={
                            <ProtectedLayout>
                                <ChangeLogPageWrapper />
                            </ProtectedLayout>
                        }
                    />
                    <Route
                        path="/admin"
                        element={
                            <ProtectedLayout>
                                <AdminPage />
                            </ProtectedLayout>
                        }
                    />

                    {/* Catch-all route for 404 - redirect to home if authenticated, login if not */}
                    <Route
                        path="*"
                        element={
                            <ProtectedLayout>
                                <Navigate to="/home" replace />
                            </ProtectedLayout>
                        }
                    />
                </Routes>
            )}
        </AppWrapper>
    );
};

export default RouterContent;
